import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type Store from 'ember-data/store';
import { resource, use } from 'ember-resources';
import LoadingSpinner from 'garaje/components/ui/classic/loading-spinner-pulse';
import type { PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type Company from 'garaje/models/company';
import type Location from 'garaje/models/location';
import type Questionnaire from 'garaje/models/questionnaire';
import { TrackedArray } from 'tracked-built-ins';

const PAGE_SIZE = 50;

export type Filters = {
  flow?: string | null;
  'include-global'?: boolean;
  'global-only'?: boolean;
  title?: string | null;
};

// Internal type for allowing user-applied filters (see public `Filters` type above) plus the
// company and location filters that are applied based on the `@company` and `@location` args.
type InternalFilters = Filters & {
  company?: string | null;
  'include-global'?: boolean;
  'global-only'?: boolean;
  location?: string | null;
};

interface VisitorsQuestionnairesListLoaderSignature {
  Args: {
    /**
     * If specified, load questionnaires for this company.
     * If both `@company` and `@location` are specified, `@company` is used.
     * One of `@company` or `@location` must be specified.
     */
    company?: Company;
    /**
     * If specified, load questionnaires for this location.
     * If both `@company` and `@location` are specified, `@company` is used.
     * One of `@company` or `@location` must be specified.
     */
    location?: Location;
    filters: Filters;
    sort: string;
  };
  Blocks: {
    empty: [];
    footer: [];
    header: [questionnaireCount: number];
    list: [questionnaires: Questionnaire[], loader: ListLoader, reload: () => void];
  };
}

type PageMetadata = {
  total: number;
};

/**
 * Handle loading questionnaires in a way that supports loading additional records without
 * resetting the list (useful for infinite scroll, for instance). `ember-infinity` doesn't play
 * well with ember-resources; returning the `ember-infinity` model from a resource was causing
 * triggering the resource to re-run immediately after the HTTP request returned.
 *
 * The reason that ember-resources is used is to be able to reset the state whenever tracked properties
 * are changed (e.g. when the user changes the filter).
 */
export class ListLoader {
  questionnaires = new TrackedArray<Questionnaire>();

  #filter: InternalFilters;
  #offset = 0;
  #sort: string;
  #store: Store;
  @tracked _meta: PageMetadata | null = null;

  constructor(store: Store, filter: InternalFilters, sort: string) {
    this.#filter = filter;
    this.#sort = sort;
    this.#store = store;
    void this.loadNextPage.perform();
  }

  loadNextPage = task(async () => {
    const result = <PaginatedRecordArray<Questionnaire>>await this.#store.query('questionnaire', {
      filter: this.#filter,
      include: [
        'flow-questionnaires',
        'flow-questionnaires.flow',
        'flow-questionnaires.flow.location',
        'location',
        'questionnaire-sections',
        'questionnaire-sections.questionnaire-fields',
        'questionnaire-sections.questionnaire-fields.questionnaire-field-options',
      ].join(','),
      page: {
        limit: PAGE_SIZE,
        offset: this.#offset,
      },
      sort: this.#sort,
    });

    this._meta = result.meta;
    const questionnairesInThisPage = result.slice();
    this.questionnaires.push(...questionnairesInThisPage);
    this.#offset += questionnairesInThisPage.length;
  });

  get hasMoreResults(): boolean {
    return this.total > this.questionnaires.length;
  }

  get isRunning(): boolean {
    return this.loadNextPage.isRunning;
  }

  get total(): number {
    return this._meta?.total ?? 0;
  }
}

export default class VisitorsQuestionnairesListLoader extends Component<VisitorsQuestionnairesListLoaderSignature> {
  @service declare store: Store;

  @tracked _reloadCount = 0;

  @use loader = resource<ListLoader>(() => {
    const filter: InternalFilters = { ...this.args.filters };
    // read _reloadCount but don't actually do anything with it; any changes to it will force a reload
    // without any changes to filters (useful, for example, after deleting a questionnaire)
    void this._reloadCount;
    if (this.args.company) {
      filter['company'] = this.args.company.id;
    } else if (this.args.location) {
      filter['location'] = this.args.location.id;
    } else {
      throw new Error('Must provide either @company or @location');
    }

    return new ListLoader(this.store, filter, this.args.sort);
  });

  get hasResults(): boolean {
    return this.loader.questionnaires.length > 0;
  }

  get questionnaires(): Questionnaire[] {
    return this.loader.questionnaires;
  }

  /**
   * Show loading state only when loading the first page.
   */
  get showLoadingSpinner(): boolean {
    return this.loader.loadNextPage.isRunning && this.questionnaires.length === 0;
  }

  @action
  reload(): void {
    this._reloadCount++;
  }

  <template>
    {{yield this.loader.total to="header"}}
    {{#if this.showLoadingSpinner}}
      <LoadingSpinner />
    {{else}}
      {{#if this.hasResults}}
        {{yield this.questionnaires this.loader this.reload to="list"}}
      {{else}}
        {{yield to="empty"}}
      {{/if}}
    {{/if}}
    {{yield to="footer"}}
  </template>
}
